import { RootStore } from "@state/stores/types";
import { DeviceDetector, UserTypes, isUserAuthenticated } from "@utils";
import { StorageStateKeysEnum } from "@utils/storage";
import { action, observable } from "mobx";
import { THEME_SETTINGS } from "@theme-settings";
import BasePopupStore from "@state/stores/popups/BasePopupStore";

class RealityCheckStore extends BasePopupStore {
	intervalId: NodeJS.Timeout | null = null;
	documentTimeStamp = new Date().getTime();

	@observable realityTime: number | null = null;

	/**
	 * iframe overlay flag is used for setting an overlay over iframes so we can track user clicks first
	 * - important for reality checks
	 * - will be set to true if document timestamp is smaller than local storage timestamp
	 */
	@observable iframeOverlay = false;

	get realityCheckTime() {
		return this.rootStore.userAuthStore.realityCheckTime;
	}

	get realityCheckBase() {
		return 10;
	}

	/**
	 * @returns reality check interval duration in seconds
	 */
	get realityCheckIntervalDuration() {
		if (
			this.rootStore.userAuthStore.user &&
			this.rootStore.userAuthStore.user?.realityCheckDuration !== null
		) {
			return this.rootStore.userAuthStore.user.realityCheckDuration * 60;
		}
		return THEME_SETTINGS.realityCheckInterval * 60;
	}

	constructor(rootStore: RootStore) {
		super(rootStore);

		if (
			!THEME_SETTINGS.isRealityCheckEnabled ||
			!THEME_SETTINGS.realityCheckInterval
		) {
			return;
		}

		this.startEventListeners();
	}

	@action.bound
	startEventListeners() {
		window.addEventListener(
			"storage",
			this.handleActiveTimeStampStorageChange
		);
		if (DeviceDetector.isDesktopTheme) {
			window.addEventListener("click", this.onClick);
		} else {
			window.addEventListener("touchstart", this.onClick);
		}
		document.addEventListener(
			"visibilitychange",
			this.initializeRealityCheck
		);
	}

	/**
	 * used for updating last active timestamp
	 */
	@action.bound
	onClick = () => {
		if (
			this.rootStore.userAuthStore.user != null &&
			!this.rootStore.realityCheckStore.isActive &&
			!this.rootStore.automaticLogoutStore.isActive
		) {
			if (!this.intervalId) {
				this.initializeRealityCheck();
			}
		} else if (
			this.rootStore.realityCheckStore.isActive ||
			this.rootStore.automaticLogoutStore.isActive
		) {
			//in case one of the popups is active, we want to turn off iframe overlay on first click/touch
			this.iframeOverlay = false;
		}
	};

	/**
	 * used for generating new timestamp and if is bigger than local storage, saved to local storage and state
	 * @returns is new timestamp saved
	 */
	@action.bound
	generateNewTimeStamp() {
		const ts = new Date().getTime();
		if (this.checkIfNewestActiveDocument(ts)) {
			this.documentTimeStamp = ts;
			this.setLastActiveTimeStamp(ts);
			return true;
		}

		return false;
	}

	/**
	 * Setting iframe overlay to true in case document time stamp is smaller than local storage time stamp
	 * @param ev Storage event
	 */
	@action.bound
	handleActiveTimeStampStorageChange(ev: StorageEvent) {
		if (ev.key == StorageStateKeysEnum.LAST_ACTIVE_TIMESTAMP) {
			if (!this.checkIfNewestActiveDocument(this.documentTimeStamp)) {
				this.resetInterval();
				this.iframeOverlay = true;
			}
		}
	}

	@action.bound
	initializeRealityCheck() {
		if (THEME_SETTINGS.isRealityCheckWithHeaderEnabled) return;

		if (
			document.visibilityState == "visible" &&
			this.isUserAuthenticated()
		) {
			const isTimeStampSaved = this.generateNewTimeStamp();
			if (isTimeStampSaved) {
				this.iframeOverlay = false;
				this.startTimingSession();
			} else {
				this.pauseTimingSession();
			}
		} else {
			this.pauseTimingSession();
		}
	}

	/**
	 * @param time current datetime in timestamp
	 * @returns true if document has newer timestamp then local storage or timestamp is not defined yet in local storage
	 */
	@action.bound
	checkIfNewestActiveDocument(time: number) {
		const lastActiveTimestamp = this.getLastActiveTimeStamp();
		return (
			lastActiveTimestamp === null ||
			(lastActiveTimestamp && time >= lastActiveTimestamp)
		);
	}

	@action.bound
	pauseTimingSession() {
		this.resetInterval();
		this.popupCheck();
	}

	startTimingSession() {
		this.resetInterval();

		if (this.realityCheckTime === 0) {
			this.pauseTimingSession();
			return;
		}

		const interval = setInterval(() => {
			const currentValue = this.realityCheckTime;

			if (
				currentValue !== null &&
				currentValue > 0 &&
				this.checkIfNewestActiveDocument(this.documentTimeStamp)
			) {
				const newValue = currentValue - this.realityCheckBase;
				this.rootStore.userAuthStore.setRealityCheckTime(newValue);
				this.updateTotalTime();
				if (newValue === 0) {
					this.pauseTimingSession();
				}
			} else {
				this.pauseTimingSession();
			}
		}, 10000);

		this.intervalId = interval;
	}

	@action.bound
	popupCheck = () => {
		const isRealityCheckExpired =
			this.rootStore.userAuthStore.realityCheckTime === 0;

		if (
			THEME_SETTINGS.isRealityCheckEnabled &&
			isUserAuthenticated() &&
			isRealityCheckExpired &&
			!this.isActive &&
			!this.rootStore.automaticLogoutStore.isActive &&
			this.totalTime
		) {
			this.activatePopup();
		} else if (!isUserAuthenticated() && this.isActive) {
			this.resetInterval();
			this.deactivatePopup();
		}
	};

	@action.bound
	onRealityCheckUpdate() {
		if (this.realityCheckTime === 0) {
			this.popupCheck();
		}
	}

	@action.bound
	extendRealityCheckTime() {
		this.rootStore.userAuthStore.setRealityCheckTime(
			this.realityCheckIntervalDuration
		);
	}

	@action.bound
	resetInterval() {
		if (this.intervalId) {
			clearInterval(this.intervalId);
		}
		this.intervalId = null;
	}

	// #region local storage getters and setters

	@action.bound
	setRealityCheckTime(realityCheckTime: number) {
		this.realityTime =
			realityCheckTime < 0 ? realityCheckTime * -1 : realityCheckTime;
	}

	@action.bound
	getLastActiveTimeStamp() {
		return JSON.parse(
			this.rootStore.localStorageProvider.get(
				StorageStateKeysEnum.LAST_ACTIVE_TIMESTAMP
			) as string
		) as number | null;
	}

	@action.bound
	setLastActiveTimeStamp(value?: number) {
		if (value) {
			this.rootStore.localStorageProvider.set(
				StorageStateKeysEnum.LAST_ACTIVE_TIMESTAMP,
				value
			);
			return;
		}

		this.rootStore.localStorageProvider.remove(
			StorageStateKeysEnum.LAST_ACTIVE_TIMESTAMP
		);
	}

	get totalTime() {
		return JSON.parse(
			this.rootStore.localStorageProvider.get(
				StorageStateKeysEnum.REALITY_CHECK_TOTAL_TIME
			) as string
		) as number | null;
	}

	@action.bound
	updateTotalTime() {
		this.rootStore.localStorageProvider.set(
			StorageStateKeysEnum.REALITY_CHECK_TOTAL_TIME,
			this.totalTime
				? this.totalTime + this.realityCheckBase
				: this.realityCheckBase
		);
	}

	@action.bound
	isUserAuthenticated(activeAccount: UserTypes = UserTypes.PRIMARY) {
		// get user token direct from local storage
		if (activeAccount == UserTypes.PRIMARY) {
			return (
				this.rootStore.localStorageProvider.get(
					StorageStateKeysEnum.USER_TOKEN
				) !== null
			);
		}
		return (
			this.rootStore.localStorageProvider.get(
				StorageStateKeysEnum.SECONDARY_TOKEN
			) !== null
		);
	}

	// #endregion local storage getters and setters

	onDispose() {
		this.pauseTimingSession();
		this.rootStore.userAuthStore.setRealityCheckTime(null);
		this.setLastActiveTimeStamp();
	}
}

export default RealityCheckStore;
